import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Trans, useTranslation } from 'next-i18next'
// Lib
import React from 'react'
import styled, { css } from 'styled-components'

// Comps
import { useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Page as PAGE } from '../components/Page'
import { EventsContext, UserContext } from '../context'
import { NookEvent } from '../types'
import { BowedHeroSection } from '../components/Sections/BowedHeroSection'
import { HowItWorksSection } from '../components/Sections/HowItWorksSection'
import { LargeExplainSection } from '../components/Sections/LargeExplainSection'
import { H3AndContentSection } from '../components/Sections/H3AndContentSection'
import { FaqSection } from '../components/Sections/FaqSection'
import { ShowCaseSection } from '../components/Sections/ShowCaseSection'
import { BowedTopBackground } from '../components/Backgrounds'
import { P } from '../components/P'
import { colors, mobileScreen, fontWeights } from '../utils'
import { px_par } from '../utils/styles'
import { Hyperlink } from '../components/Hyperlink'
import { PreviewEarningsCalculatorSection } from '../components/Sections/PreviewEarningsCalculatorSection'
import { useAnalyticsEvent } from '../hooks/analyticsEvent'
import { PageAnchor } from '../components/PageAnchor'

// Glue Spacing Styles
const Page = styled(PAGE)`
  .BowedHero {
    .heroText {
      max-width: 670px !important;
    }
  }

  .LargeExplainSection {
    padding-top: 95px;
    padding-bottom: 95px;
  }

  .H3AndContentSection {
    padding-top: calc(
      100px - 25px
    ); /** Because with the animation below it feels wrong */
    padding-bottom: 60px;

    ${mobileScreen(css`
      padding-top: 45px;
      padding-bottom: 80px;
    `)}
  }

  .bowed-up {
    &:before {
      /* Set the negative margin for this bg */
      margin-bottom: ${px_par(1300, 300)};
    }

    .HowItWorksSection {
      a {
        font-weight: ${fontWeights.bold};
      }
    }
    .ShowCaseSection {
    }
  }

  .FaqSection {
    padding-top: 95px;
    padding-bottom: 120px;

    ${mobileScreen(css`
      padding-top: 60px;
      padding-bottom: 60px;
    `)}
  }
`

// Defs
interface Props {}

const PaiedEventsPage: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')
  // Global State
  const router = useRouter()
  const { events, fetchEvents, resetCurrentEventPage } =
    useContext(EventsContext)
  const { currentUser } = useContext(UserContext)

  const analyticsEvent = useAnalyticsEvent()

  // Local State
  const [eventsToShow, setEventsToShow] = useState<NookEvent[]>([])

  // Effects
  useEffect(() => {
    setEventsToShow(events)
  }, [events])

  useEffect(() => {
    if (!events || events.length < 4) {
      fetchEvents({ category: 'allCategory' })
    } else {
      setEventsToShow(events)
    }

    analyticsEvent.websiteVisit('earn')
  }, [])

  useEffect(() => {
    if (currentUser === null) {
      resetCurrentEventPage(true)
    }
  }, [currentUser])

  // Funcs
  const onCtaClick = () => {
    router.push('/signup')
  }

  // ..
  return (
    <Page>
      <BowedHeroSection
        bgImage={'/images/earnHero.jpg'}
        smallHeader={t(`how-to-earn677816473`, `MONETISE YOUR ONLINE EVENTS`)}
        largeHeader={t(
          `how-to-earn1783698271`,
          `Share your passion and start earning today.`,
        )}
        ctaText={t(`Host your event`, `Host your event`)}
        showCTA={true}
        onCtaClick={onCtaClick}
      />

      <LargeExplainSection
        text={t(
          'how-to-earn20211224153918810692431',
          `Creators deserve the perfect platform to share their craft, interests, passions, lessons and professions with the world. You can earn money from your events – starting right now.`,
        )}
      />

      <BowedTopBackground className="bowed-up" background={colors.washedPurple}>
        <HowItWorksSection
          quote={t(`How it works`, `How it works`)}
          items={[
            {
              title: t('Create your account', 'Create your account'),
              content: t(
                'how-to-earn20211224154023347267755',
                'Make it official! Create a discoverable group page for your community to follow.',
              ),
            },
            {
              title: t('Set up your payments', 'Set up your payments'),
              content: (
                <Trans i18nKey="how-to-earn20211224154106003478634">
                  Create or connect a{' '}
                  <a
                    href="https://stripe.com/"
                    target="_blank"
                    rel="noreferrer">
                    Stripe
                  </a>
                  account to manage earnings and cash out after your events.
                </Trans>
              ),
            },
            {
              title: t('Start earning money', 'Start earning money'),
              content: t(
                'how-to-earn20211224154237211730357',
                "Create an event in Klyk, nominate your ticket price, and you're ready to start promoting!",
              ),
            },
          ]}
        />

        <ShowCaseSection
          bgImage={'/images/paidevents-hero.png'}
          parallaxY={['-50px', '100px']}
          hideRightImage={true}
          hideLeftImage={false}
          leftImage={'/images/earnHeroImageLeft.png'}
        />
      </BowedTopBackground>

      <H3AndContentSection
        title={t(`Earning with Klyk`, `Earning with Klyk`)}
        text={t(
          'how-to-earn20211224154323323943506',
          `We have partnered with Stripe to create a seamless experience, making it easier to manage your earnings with total safety and security. When people purchase tickets for your event, a small transaction fee will be automatically deducted. You will also be charged a payout fee through Stripe, also deducted from your earnings so you’re never out of pocket. We know that for some, this is more than just a side hustle – and we value pricing transparency across our platform.`,
        )}
      />

      <PageAnchor anchor={t(`earnings-calculator`, `earnings-calculator`)}>
        <PreviewEarningsCalculatorSection />
      </PageAnchor>

      <FaqSection
        questions={[
          {
            question: t(
              'how-to-earn20211224154357300073363',
              'I like to run events under my own name, rather than as a group or company. Can I run paid events on Klyk?',
            ),
            answer: (
              <P>
                {t(
                  `how-to-earn823671941`,
                  `Absolutely! Both groups and individuals use Klyk. The difference is – if you’re an individual creator, your "group" in Klyk is simply your following or community. We'll help you link a Stripe account as soon as you create your first paid event.`,
                )}
              </P>
            ),
          },
          {
            question: t(
              'how-to-earn20211224154419512224186',
              'How much will I be charged if I add a ticket price to my event?',
            ),
            answer: (
              <P>
                <Trans i18nKey="how-to-earn20211224154459716398268">
                  We apply a transaction fee starting from 7.9% + USD 30c on all
                  ticket sales (final amount depending on the ticket buyer’s and
                  seller&apos;s country – please refer to the fee scale link{' '}
                  <Hyperlink href="/terms" newTab={true}>
                    here
                  </Hyperlink>
                  ). Stripe will also charge an ongoing active of USD $2 per
                  month, and an account payout fee of 0.25% + USD 25c per
                  transaction.
                </Trans>
              </P>
            ),

            // <P>A transaction >fee</Hyperlink> starting from 7.9% + USD 30c will be applied on all ticket sales (final amount depending on the ticket buyers and sellers country – pleas refer to the fee scale link here). Stripe will also charge the account holder an ongoing active of USD $2 per month, and an account payout fee of 0.25% + USD 25c per transaction.</P>
          },
          {
            question: t(
              'how-to-earn20211224154635288889653',
              `I need to cancel my paid event. What is Klyk's refund policy?`,
            ),
            answer: (
              <P>
                {t(
                  `how-to-earn1281233255`,
                  `Your attendees will receive a full refund for the amount paid, minus transaction fees. Cancelling an event as host is a fuss-free experience too: your attendees will be refunded the full ticket amount minus transaction fees automatically, and you’ll have the option to include your own explanation in the cancellation email.`,
                )}
              </P>
            ),
          },
          {
            question: t(
              'how-to-earn20211224154653096916578',
              'Can attendees change their minds about attending my event?',
            ),
            answer: (
              <P>
                {t(
                  `how-to-earn2122456890`,
                  `They can! Our priority is making sure you have a full house of engaged attendees, so your ticketholders stay eligible for a refund up until 24 hours before your event. They will be refunded the cost of your ticket upon their cancellation through Klyk, minus any transaction fees. If attendees cancel within 24hrs of your event, the cost of their ticket is forfeited.`,
                )}
              </P>
            ),
          },
        ]}
      />
    </Page>
  )
}

// Export.
export default PaiedEventsPage
export async function getStaticProps({ locale }) {
  const translations = await serverSideTranslations(locale, ['common'])
  return {
    props: {
      ...translations,
    },
  }
}
